import {
  Box,
  Button,
  CircularProgress,
  H2,
} from '@ouster/webviz-react-components';

import { useEffect, useRef, useState } from 'react';
import { RiCheckboxCircleLine, RiErrorWarningLine } from 'react-icons/ri';
import { useNavigate } from 'react-router';
import { useStoreActions, useStoreState } from 'src/reducers';

type AcceptStatus = 'processing' | 'success' | 'error';

const errorMessage = (err: unknown, fallback: string) => {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  if (typeof err === 'string' && err) {
    return err;
  }
  return fallback;
};

export default function AcceptInvitation() {
  const { user, appUser } = useStoreState((storeState) => storeState.user);
  const { addInvitedUser } = useStoreActions((actions) => actions.organization);
  const { fetchUserOrgs, setCurUserOrg } = useStoreActions(
    (actions) => actions.user
  );
  const navigate = useNavigate();
  const hasProcessedRef = useRef(false);

  const [status, setStatus] = useState<AcceptStatus>('processing');
  const [title, setTitle] = useState('Accepting invitation');
  const [message, setMessage] = useState(
    'Please wait while we add you to the organization.'
  );
  const [acceptedOrgId, setAcceptedOrgId] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.identity_id || !appUser || hasProcessedRef.current) {
      return;
    }

    hasProcessedRef.current = true;

    const searchParams = new URLSearchParams(window.location.search);
    const organizationUuid = searchParams.get('organization_uuid');
    const invitationUuid = searchParams.get('invitation_uuid');
    const organizationIdParam = searchParams.get('organization_id');
    const organizationId = organizationIdParam
      ? Number.parseInt(organizationIdParam, 10)
      : NaN;

    if (!organizationUuid || !invitationUuid) {
      // One-time URL sync guarded by hasProcessedRef; kicks off async accept
      // only for the valid-params path below.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStatus('error');
      setTitle('Invitation not processed');
      setMessage('Missing required invitation parameters in this URL.');
      return;
    }

    const processInvitation = async () => {
      try {
        await addInvitedUser({
          organization_uuid: organizationUuid,
          invitation_id: invitationUuid,
        });
      } catch (err) {
        setStatus('error');
        setTitle('Invitation not processed');
        setMessage(
          errorMessage(
            err,
            'Unable to accept this invitation. It may be expired or already used.'
          )
        );
        return;
      }

      if (Number.isFinite(organizationId)) {
        localStorage.setItem('lastOrgId', organizationId.toString());
        setAcceptedOrgId(organizationId);
      }

      // Membership is already created at this point. Treat follow-up org
      // refresh/switch failures as non-fatal so the user still sees success.
      try {
        await fetchUserOrgs(user.identity_id);

        if (Number.isFinite(organizationId)) {
          setCurUserOrg(organizationId);
        }
      } catch (err) {
        console.error('Failed to refresh orgs after accepting invitation', err);
      }

      setStatus('success');
      setTitle('Invitation accepted');
      setMessage(
        'You have joined the organization. You can continue to your dashboard.'
      );
    };

    void processInvitation();
  }, [
    addInvitedUser,
    appUser,
    fetchUserOrgs,
    setCurUserOrg,
    user?.identity_id,
  ]);

  if (!user) {
    return null;
  }

  return (
    <Box
      h="full"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="24px"
      py="32px"
      color="white"
    >
      <Box
        width="100%"
        maxWidth="760px"
        border="1px solid var(--ouster-colors-whiteAlpha-300)"
        borderRadius="12px"
        p="32px"
        display="grid"
        rowGap="16px"
        textAlign="center"
        bg="var(--ouster-colors-whiteAlpha-50)"
      >
        {status === 'processing' ? (
          <Box display="grid" rowGap="12px">
            <H2 fontSize="30px" fontWeight={500}>
              {title}
            </H2>
            <Box display="grid" placeItems="center">
              <CircularProgress isIndeterminate size="54px" thickness={10} />
            </Box>
          </Box>
        ) : (
          <Box display="grid" rowGap="12px" placeItems="center">
            <H2 fontSize="30px" fontWeight={500}>
              {title}
            </H2>
            {status === 'error' ? (
              <RiErrorWarningLine
                size={52}
                color="var(--ouster-colors-red-200)"
              />
            ) : (
              <RiCheckboxCircleLine
                size={52}
                color="var(--ouster-colors-green-200)"
              />
            )}
          </Box>
        )}

        <Box
          as="p"
          fontSize="18px"
          lineHeight="1.65"
          color={
            status === 'error'
              ? 'var(--ouster-colors-red-200)'
              : 'var(--ouster-colors-whiteAlpha-900)'
          }
          style={{ textWrap: 'balance' }}
        >
          {message}
        </Box>

        {status !== 'processing' ? (
          <Box display="grid" placeItems="center" mt="8px">
            <Button
              variant="solid"
              size="lg"
              rounded="md"
              onClick={() => {
                if (status === 'success' && acceptedOrgId != null) {
                  navigate('/drive/private');
                  return;
                }
                navigate('/drive/private');
              }}
            >
              Return to Dashboard
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
